/**
 * Factory State Atoms
 *
 * Simplified Jotai atoms for basic factory state management:
 * - Current factory selection
 * - Basic factory data
 * - Simple loading state
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Factory } from '@/schemas/factory';

// Current selected factory ID (persisted)
export const currentFactoryIdAtom = atomWithStorage<string | null>('current-factory-id', null);

// Current factory data (derived from server state) - writable for reset functionality
export const currentFactoryAtom = atom<Factory | null>(null);

// Simple loading state - writable for reset functionality
export const factoryLoadingAtom = atom<boolean>(false);

// Factory error state - writable for reset functionality
export const factoryErrorAtom = atom<string | null>(null);

// Available factories for current user (derived from auth/queries)
export const availableFactoriesAtom = atom<Factory[]>([]);

// Helper to check if multiple factories are available
export const isMultiTenantAtom = atom(get => {
  const availableFactories = get(availableFactoriesAtom);
  return availableFactories.length > 1;
});

// Currently selected factory object (derived from available factories + selected ID)
export const currentFactoryDerivedAtom = atom<Factory | undefined>(get => {
  const factoryId = get(currentFactoryIdAtom);
  return get(availableFactoriesAtom).find(f => f.id === factoryId);
});

// Basic factory display info
export const factoryDisplayInfoAtom = atom(get => {
  const factory = get(currentFactoryDerivedAtom);

  if (!factory) {
    return null;
  }

  return {
    name: factory.name,
    location: `${factory.location.city}, ${factory.location.country}`,
    timezone: factory.settings.timezone,
  };
});

// Factory timezone helper
export const factoryTimeZoneAtom = atom(get => {
  return get(currentFactoryDerivedAtom)?.settings.timezone ?? 'UTC';
});

// Simple reset helper - only resets storage and loading state
export const resetFactoryStateAtom = atom(null, (_get, set) => {
  set(currentFactoryIdAtom, null);
  set(factoryLoadingAtom, false);
  // Note: Error state should be managed by query error handling
  // Note: currentFactoryAtom and availableFactoriesAtom should be managed by queries/server state
});
